/**
 * The Creative Genome: what it is, whether this artist can have one yet, and the button.
 *
 * It gates the social profile, so it has to explain itself rather than just refuse. Three states
 * an artist can actually be in:
 *
 *   short of the floor  say how many more works, and why more work makes a better result
 *   eligible            offer the button, and say what it will read
 *   running             say it is reading their work, not "processing"
 */
import { Loader2, Palette, Sparkles, Upload } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'

import { useGenerateGenome, useGenomeStatus } from '../hooks/useGenome'

export function GenomeCard({ compact = false }: { compact?: boolean }) {
  const { data, isLoading } = useGenomeStatus()
  const generate = useGenerateGenome()

  if (isLoading) return <Skeleton className="h-40 w-full" />
  if (!data) return null

  const running = data.status === 'pending'
  const pct = Math.min(100, Math.round((data.artworkCount / data.required) * 100))

  return (
    <Card>
      <CardHeader className={compact ? 'pb-3' : undefined}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Creative Genome
            </CardTitle>
            <CardDescription>
              The palette, motifs and voice of your work — measured from your own images, not
              guessed. Everything Arthaus generates for you is built from it.
            </CardDescription>
          </div>
          {data.hasGenome && !running && <Badge>Ready</Badge>}
          {running && <Badge variant="secondary">Reading your work</Badge>}
          {data.status === 'stalled' && <Badge variant="outline">Interrupted</Badge>}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!data.eligible && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {data.artworkCount} of {data.required} artworks uploaded
              </span>
              <span className="font-medium tabular-nums">{pct}%</span>
            </div>
            <Progress value={pct} />
            <p className="text-sm text-muted-foreground">
              Upload {data.remaining} more {data.remaining === 1 ? 'work' : 'works'} to unlock it.
              A genome read from a handful of pieces describes those pieces; read from a body of
              work, it describes your practice.
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/app/upload">
                <Upload className="h-4 w-4 mr-2" />
                Upload artwork
              </Link>
            </Button>
          </div>
        )}

        {data.eligible && running && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
            Reading {data.artworkCount} works for colour, motif and voice. This takes a minute or
            two — you can leave this page.
          </p>
        )}

        {data.eligible && !running && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {data.hasGenome
                ? 'Derived from your work. Regenerate it if your practice has moved on since.'
                : data.status === 'stalled'
                  ? 'The last attempt did not finish. Nothing was lost — try again.'
                  : `Ready to read your ${data.artworkCount} uploaded works.`}
            </p>
            <Button onClick={() => generate.mutate()} disabled={generate.isPending}>
              {generate.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              {data.hasGenome ? 'Regenerate genome' : 'Generate my genome'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
